import type { DocumentFileRow } from './salesOrder';

/**
 * 🔒 What a document's page reads beside its own answer (piece 22; decision
 * 0457, T1071): the files attached to it, for a kind whose page answer does not
 * already carry them, and the messages tagged to it (decision 1663, T1072).
 * One route for every document kind, so a page answer owned by another module
 * is not reopened to carry one more list.
 */
export const DOCUMENT_ATTACHMENTS_PATH = '/document-attachments';

/**
 * The document kinds whose page reads this route: every kind with a page of its
 * own and a Files panel on it. The order is the order piece 22 lists them in,
 * then the Work Order, whose PDF view's rail draws the Files tab (T2984;
 * decision 6800, piece 29), then the Vendor Credit, whose page draws Files
 * since decision 7580.
 */
export const DOCUMENT_ATTACHMENT_KINDS = ['Quote', 'SalesOrder', 'Invoice', 'Bill', 'Payment', 'PurchaseOrder', 'CreditNote', 'ChangeOrder', 'RFQ', 'WorkOrder', 'VendorCredit'] as const;
export type DocumentAttachmentKind = (typeof DOCUMENT_ATTACHMENT_KINDS)[number];

export function isDocumentAttachmentKind(value: string): value is DocumentAttachmentKind {
  return (DOCUMENT_ATTACHMENT_KINDS as readonly string[]).includes(value);
}

/**
 * The route for one document. `withFiles` asks for the uploaded files too; a
 * kind whose page answer already carries them leaves it off, so one file is
 * never read two ways on one page.
 */
export function documentAttachmentsPath(kind: DocumentAttachmentKind, documentId: string, withFiles: boolean): string {
  return `${DOCUMENT_ATTACHMENTS_PATH}/${kind}/${encodeURIComponent(documentId)}${withFiles ? '?files=1' : ''}`;
}

/** One attachment of a tagged message, named read-only on the document (decision 1663). */
export interface DocumentMailAttachment {
  id: string;
  filename: string;
  /** 🔒 The scan held it back (decision 0234): named, never offered. */
  blocked: boolean;
}

/**
 * 🔒 One message tagged to a document, as the document's Files block lists it
 * under From mail (piece 32; decision 1663): its subject, its sender, its date
 * and its direction, and where the mail client opens it.
 */
export interface DocumentMailRow {
  id: string;
  /** The folder the mail client opens it in, beside its own id. */
  folderId: string;
  subject: string;
  /** The sender's display name where the message carries one, else the address. */
  from: string;
  fromAddress: string;
  direction: 'Inbound' | 'Outbound';
  /** When it arrived or went: received, else sent, else written. */
  at: string;
  attachments: DocumentMailAttachment[];
}

/** The page's read: the files, or null where they were not asked for, and the tagged mail the viewer may read (decision 1663). */
export interface DocumentAttachmentsAnswer {
  files: DocumentFileRow[] | null;
  mail: DocumentMailRow[];
}

/** 🔒 The message's own address in the mail client, opened on its folder (decision 1663). */
export function documentMailLink(row: Pick<DocumentMailRow, 'id' | 'folderId'>, mailRoute: string): string {
  return `${mailRoute}?${new URLSearchParams({ folder: row.folderId, message: row.id }).toString()}`;
}
